import { isPlatformBrowser } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  PLATFORM_ID,
  provideAppInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { AppellateTranslateCompiler, basicInterceptor, DynamicLocaleId, isBackendPath, UniversalTranslateLoader } from '@gs/shared';
import { LanguageService, provideImageLoader } from '@gs/shared/services';
import { provideTranslateCompiler, provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { GALLERY_SLIDE_COMPONENTS_REGISTRY_PROVIDER } from '@gs/shared/services/components-helpers/gallery-slide.components.registry';
import { PAGE_COMPONENTS_REGISTRY_PROVIDER } from '@gs/shared/services/components-helpers/page.components.registry';
import { routes } from './app.routes';
import { authInterceptor } from './interceptors/auth/auth.interceptor';
import { OCS_BASKET_OPEN_HEADER_KEY, pluginInterceptor } from './interceptors/plugin/plugin.interceptor';
import { ssrTimeoutInterceptor } from './interceptors/ssr-timeout.interceptor';
import { trackingInterceptor } from './interceptors/tracking/tracking.interceptor';
import { CmsPrefetchService } from './services/cms-prefetch.service';

// f/cms/init/ reaches the browser through its own TransferState entry (InformationService)
const excludeCachingForUrls = ['api/f/setup/', 'api/f/config/', 'api/f/cms/init/'];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideImageLoader(),
    { provide: LOCALE_ID, useFactory: (lang: LanguageService) => new DynamicLocaleId(lang), deps: [LanguageService] },
    provideClientHydration(
      // Clicks and inputs on the server-rendered page before the hydration are replayed afterwards instead of lost
      withEventReplay(),
      withHttpTransferCacheOptions({
        includePostRequests: true,
        // The plugin interceptor reads the basket open header of the transferred responses
        includeHeaders: [OCS_BASKET_OPEN_HEADER_KEY],
        filter: (req) => {
          return !excludeCachingForUrls.some((url) => req.url.includes(url));
        }
      })
    ),
    provideHttpClient(withInterceptors([authInterceptor, basicInterceptor, pluginInterceptor, ssrTimeoutInterceptor, trackingInterceptor])),
    provideRouter(routes),
    // Prefetch the current page's CMS data during bootstrap (browser only) — before the router resolves
    // the lazy page route — so the FCP/LCP-gating response arrives ~1s sooner. Skipped for the backend
    // paths (/admin, /authorize), which redirect out and never render a CMS page.
    provideAppInitializer(() => {
      const platformId = inject(PLATFORM_ID);
      if (!isPlatformBrowser(platformId)) return;
      const { pathname, href } = window.location;
      if (isBackendPath(pathname)) return;
      inject(CmsPrefetchService).prefetch(href);
    }),
    PAGE_COMPONENTS_REGISTRY_PROVIDER,
    GALLERY_SLIDE_COMPONENTS_REGISTRY_PROVIDER,
    provideTranslateService({
      loader: provideTranslateLoader(() => new UniversalTranslateLoader('./i18n/', '.json')),
      compiler: provideTranslateCompiler(AppellateTranslateCompiler)
    })
  ]
};
